import {
  PlatformTableData,
  PlatformTableFiltersParameter,
  PlatformTableSortDirection,
  PlatformTableListRowsTeamParams,
} from '@cloudquery/api-platform';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import qs from 'qs';

import {
  CLOUD_ASSET_TABLE_NAME,
  getTablePerPageState,
  parseFindComparisonExpressions,
  FilterOperator,
} from 'shared/base';

import { CLOUD_ASSET_UNIQUE_KEY } from '../constants/data';
import { AssetInventorySidebarFilterFieldEnum } from '../constants/sidebar';
import {
  DefaultPaginationModel,
  menuBase,
  TableStateUrlParam,
  UNCATEGORIZED_CATEGORY_VALUE,
  ALL_DATA_CATEGORY_VALUE,
} from '../constants/tableState';
import { defaultContext } from '../contexts/tableState/context';
import {
  FilterFieldProps,
  TableValidationContext,
  DisplayColumns,
  TableSort,
  QSTableState,
  TableState,
  AssetResourceCategoryMenuItem,
  TableStateValuesUnion,
  DataFilterString,
} from '../types';

const wrapValue = (value: string) => `"${value}"`;

export const conditionallyWrapValue = (value: string) => {
  const quotedValueRegex = /^".*"$/;
  if (quotedValueRegex.test(value)) {
    return value;
  }

  return wrapValue(value);
};

export const conditionallyQuoteWrapTags = (value: string) => {
  const quotedValueRegex = /^".*"$/;
  if (quotedValueRegex.test(value)) {
    return value;
  }

  const nonAlphaRegex = /[^a-zA-Z]/;

  return value && nonAlphaRegex.test(value)
    ? value
        .split(': ')
        .map((segment) => wrapValue(segment))
        .join(': ')
    : value;
};

// [PURPOSE]: Identify lone EQUAL/NOT EQUAL key/value pairs in the filter string
// - If a single equal/not equal operator exists, returns the value
// - If multiple keys exist, returns undefined
// - If no keys exist, returns undefined
export const getValueFromString = (filterString: string, key: string): string | undefined => {
  const results = parseFindComparisonExpressions(filterString);

  const equalityResults = results.filter(
    ({ field, operator }) => field === key && (operator === '=' || operator === '!='),
  );

  if (equalityResults.length === 1) {
    return equalityResults[0].value;
  }

  return undefined;
};

export const getTableNameFromFilter = (filter: DataFilterString): string => {
  const resourceType = getValueFromString(
    filter,
    AssetInventorySidebarFilterFieldEnum.resource_type,
  )?.replace(new RegExp(`^['"]+|['"]+$`, 'g'), '');

  return resourceType ?? CLOUD_ASSET_TABLE_NAME;
};

export function cleanFilterString(filterString: string) {
  let cleaned = filterString.trim();

  // Remove empty parentheses
  cleaned = cleaned.replaceAll(/\(\s*\)/g, '');

  // Handle adjacent "AND"/"OR" (remove the right one)
  const adjacentLogicOps = /\b(AND|OR)\s+(?:AND|OR)\b/gi;
  while (adjacentLogicOps.test(cleaned)) {
    cleaned = cleaned.replaceAll(adjacentLogicOps, '$1');
  }

  // Remove leading "AND"/"OR"
  cleaned = cleaned.replaceAll(/^\s*(AND|OR)\s+/gi, '');

  // Remove trailing "AND"/"OR"
  cleaned = cleaned.replaceAll(/\s+(AND|OR)\s*$/gi, '');

  // Remove lone "AND"/"OR"
  if (/^\s*(AND|OR)\s*$/i.test(cleaned)) {
    cleaned = '';
  }

  return cleaned;
}

export const deleteKeysFromString = (filterString: string, keys: string[]) => {
  const results = parseFindComparisonExpressions(filterString);
  let newFilterString = filterString;

  const filteredResults = results.filter(({ field }) => keys.includes(field));

  for (const result of filteredResults) {
    newFilterString = newFilterString.replace(result.text, '');
  }

  return cleanFilterString(newFilterString);
};

export const setKeyValuesOnString = (
  filterString: string,
  keyValues: [string, string?, FilterOperator?][],
): string => {
  const results = parseFindComparisonExpressions(filterString);
  let newFilterString = filterString;

  for (const [key, value, operator = FilterOperator.equal] of keyValues) {
    const targetResult = results.find(({ field }) => field === key);
    if (targetResult) {
      newFilterString = value
        ? newFilterString.replace(targetResult.text, `${key} ${operator} ${value}`)
        : deleteKeysFromString(newFilterString, [key]);
    } else if (value) {
      newFilterString = [newFilterString, `AND ${key} ${operator} ${value}`].join(' ');
    }
  }

  return cleanFilterString(newFilterString);
};

export const displayFilterString = (filter?: DataFilterString | null): string => {
  return filter ?? '';
};

export const displayFilterNodes = (filter?: DataFilterString | null): string[] => {
  return [displayFilterString(filter)];
};

export const checkSavedFilterEquality = (
  savedFilter: DataFilterString,
  currentFilter: DataFilterString,
) => {
  return savedFilter === currentFilter;
};

export const validateDisplayColumns = (
  displayColumns: DisplayColumns,
  context: TableValidationContext,
): DisplayColumns => {
  const validatedDisplayColumns = new Set<string>();

  if (displayColumns.size > 0) {
    for (const column of displayColumns) {
      validatedDisplayColumns.add(column);
    }
  } else {
    for (const column of context.defaultDisplayColumns ?? []) {
      validatedDisplayColumns.add(column);
    }
  }

  return validatedDisplayColumns;
};

export const validateSort = (sort: TableSort, context: TableValidationContext): TableSort => {
  return sort.filter((sortItem) => {
    const isValidDirection = sortItem.sort === 'asc' || sortItem.sort === 'desc';
    const isValidField = context.columns.some((col) => col.field === sortItem.field);

    return isValidDirection && isValidField;
  });
};

function getSortFromTableName(tableName: string): TableSort {
  return tableName === CLOUD_ASSET_TABLE_NAME
    ? [
        {
          field: 'resource_type',
          sort: 'asc',
        },
      ]
    : [
        {
          field: '_cq_name',
          sort: 'asc',
        },
      ];
}

export const toTableState = (
  qsTableState: QSTableState | undefined,
  context: TableValidationContext,
  tableId: string,
): TableState => {
  const originalFilter = qsTableState?.filterId ? { id: qsTableState.filterId } : undefined;
  const filter = qsTableState?.filter ?? defaultContext.filter;
  const stateSort = qsTableState?.sort
    ? validateSort(
        Object.entries(qsTableState?.sort ?? {}).map(([field, sort]) => ({
          field,
          sort,
        })),
        context,
      )
    : getSortFromTableName(getTableNameFromFilter(filter));

  return {
    detailIds: qsTableState?.detailIds,
    filter,
    originalFilter,
    pagination: qsTableState?.pagination
      ? {
          page: Number(qsTableState.pagination.page),
          pageSize: Number(qsTableState.pagination.pageSize),
        }
      : {
          ...DefaultPaginationModel,
          pageSize: getTablePerPageState(tableId) ?? DefaultPaginationModel.pageSize,
        },
    sort: stateSort.length > 0 ? stateSort : defaultContext.sort,
  };
};

export const decodeSearchParams = (
  searchParams: URLSearchParams,
  context: TableValidationContext,
  tableId: string,
): TableState => {
  const parsedParams = qs.parse(searchParams.toString()) as QSTableState;

  // Handling to remove the old filter shape if recieved from the query params
  parsedParams.filter = typeof parsedParams.filter === 'string' ? parsedParams.filter : '';

  const ts = toTableState(parsedParams, context, tableId);

  return ts;
};

export const manageDisplayColumns = (
  toggleColumn: string,
  displayColumns: DisplayColumns,
): DisplayColumns => {
  const copiedDisplayColumns = new Set(displayColumns) as DisplayColumns;
  if (copiedDisplayColumns.has(toggleColumn)) {
    copiedDisplayColumns.delete(toggleColumn);
  } else {
    copiedDisplayColumns.add(toggleColumn);
  }

  return copiedDisplayColumns.size > 0 ? copiedDisplayColumns : (new Set() as DisplayColumns);
};

export const manageSort = (newSortModel: GridSortModel): GridSortModel => {
  return newSortModel.length > 0 ? newSortModel : [];
};

export const managePagination = (
  newPaginationModel?: GridPaginationModel,
  prevPaginationModel?: GridPaginationModel,
): GridPaginationModel => {
  if (newPaginationModel) {
    if (prevPaginationModel && newPaginationModel.pageSize !== prevPaginationModel.pageSize) {
      newPaginationModel.page = 0;
    }

    return newPaginationModel;
  } else {
    return DefaultPaginationModel;
  }
};

const toQSTableState = (tableState: TableState): QSTableState => {
  const copiedTableState = { ...tableState };
  const sort: QSTableState['sort'] = {};
  for (const sortItem of copiedTableState.sort) {
    sort[sortItem.field] = sortItem.sort;
  }

  return {
    detailIds: copiedTableState.detailIds,
    filter: copiedTableState.filter,
    filterId: copiedTableState.originalFilter?.id,
    pagination: copiedTableState.pagination,
    sort,
  };
};

export const encodeSearchParams = (newState: TableState, searchParams: URLSearchParams): string => {
  const normalizedNewState = toQSTableState(newState);
  const existingParams = qs.parse(searchParams.toString());

  const newQuery = qs.stringify(Object.assign(existingParams, normalizedNewState));

  return newQuery;
};

export const dataFilterToApiFilter = (
  dataFilter: DataFilterString,
  tableName: string,
): { filter: PlatformTableFiltersParameter } | undefined => {
  if (dataFilter.length === 0) {
    return undefined;
  }

  return { filter: [buildValidFilterString(dataFilter, tableName)] };
};

export const buildValidFilterString = (filterString: string, tableName: string): string => {
  if (tableName === CLOUD_ASSET_TABLE_NAME) {
    return filterString;
  }

  // remove AssetInventorySidebarFilterFieldEnums from the query when querying against resource_type tables
  return deleteKeysFromString(filterString, [
    AssetInventorySidebarFilterFieldEnum.resource_category,
    AssetInventorySidebarFilterFieldEnum.resource_type,
  ]);
};

export const sortModelToApiSortProps = (
  sortModel: GridSortModel,
  tableName: string,
): Pick<PlatformTableListRowsTeamParams, 'sort_by' | 'sort_dir'> | undefined => {
  if (!sortModel || sortModel.length === 0) {
    const defaultSort = getSortFromTableName(tableName);

    return {
      sort_by: defaultSort.map(({ field }) => field),
      sort_dir: defaultSort.map(({ sort }) => sort as PlatformTableSortDirection),
    };
  } else {
    const sortProps: {
      sort_by: string[];
      sort_dir: PlatformTableSortDirection[];
    } = {
      sort_by: [],
      sort_dir: [],
    };

    for (const sortItem of sortModel) {
      if (['asc', 'desc'].includes(sortItem.sort as PlatformTableSortDirection)) {
        sortProps.sort_by.push(sortItem.field);
        sortProps.sort_dir.push(sortItem.sort as PlatformTableSortDirection);
      }
    }

    return sortProps.sort_by.length > 0 ? sortProps : undefined;
  }
};

export const resourceCategoriesToFilterFields = (
  resourceCategories?: AssetResourceCategoryMenuItem[],
): FilterFieldProps[] => {
  return resourceCategories
    ? [
        {
          field: AssetInventorySidebarFilterFieldEnum.resource_category,
          label: 'Resource Category',
          options: resourceCategories.map((item) => ({
            count: item.count,
            label: item.label,
            value: item.value,
          })),
          type: 'string',
        },
        {
          field: AssetInventorySidebarFilterFieldEnum.resource_type_label,
          label: 'Resource Type',
          options: resourceCategories
            .flatMap((item) =>
              item.subitems?.map((subitem) => ({
                count: subitem.count,
                label: subitem.label,
                value: subitem.value,
              })),
            )
            .filter(Boolean) as any,
          type: 'string',
        },
      ]
    : [];
};

const getMenuItems = (hideAllData: boolean) => {
  const menuItems = menuBase.map((menuItem) => ({ ...menuItem, count: 0, subitems: [] as any }));

  if (hideAllData) {
    return menuItems.filter(({ value }) => value !== ALL_DATA_CATEGORY_VALUE);
  }

  return menuItems;
};

export const convertResourcesDataToMenuItem = (
  rows: PlatformTableData['rows'],
  hideAllData: boolean,
): {
  menuItems: AssetResourceCategoryMenuItem[];
  resourceCategoriesTotalCount: number;
} => {
  let resourceCategoriesTotalCount = 0;
  const menuItems = getMenuItems(hideAllData);
  const allDataMenuItem = menuItems.find(({ value }) => value === ALL_DATA_CATEGORY_VALUE);

  for (const row of rows) {
    const [cloud, resourceCategory, resourceTypeLabel, resourceTypeValue, count] = row as [
      string,
      string,
      string,
      string,
      number,
    ];

    const menuItem = menuItems.find(({ value }) => value === resourceCategory);
    const isCategorized =
      resourceCategory?.length && resourceCategory !== UNCATEGORIZED_CATEGORY_VALUE;
    if (isCategorized) resourceCategoriesTotalCount += count;

    if (menuItem) {
      menuItem.count = menuItem.count + count;

      menuItem.subitems?.push({
        cloud,
        count,
        label: resourceTypeLabel,
        value: resourceTypeValue,
      });
    }

    if (allDataMenuItem) {
      allDataMenuItem.count += count;
      allDataMenuItem.subitems?.push({
        cloud,
        count,
        label: resourceTypeLabel,
        value: resourceTypeValue,
      });
    }
  }

  return { menuItems, resourceCategoriesTotalCount };
};

export const addQueryWhere = (query: string, cqId?: string): string => {
  if (!cqId) {
    return query;
  }

  return `${query} WHERE ${CLOUD_ASSET_UNIQUE_KEY}::text = '${cqId}'`;
};

export const constructNewTableState = ({
  filter,
  newEntry,
  tableState,
  type,
}: {
  filter: DataFilterString;
  newEntry: TableStateValuesUnion | undefined;
  tableState: TableState;
  type: TableStateUrlParam;
}): TableState => {
  const newState = { ...tableState, [type]: newEntry };

  if (type === TableStateUrlParam.Filter) {
    newState[TableStateUrlParam.Pagination] = DefaultPaginationModel;

    if (
      getValueFromString(
        newEntry as DataFilterString,
        AssetInventorySidebarFilterFieldEnum.resource_type,
      ) !== getValueFromString(filter, AssetInventorySidebarFilterFieldEnum.resource_type)
    ) {
      newState[TableStateUrlParam.Sort] = [];
    }
  }

  return newState;
};
